import { HubSpread, Forms, SegmentedControl } from '@ofself/shell';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import RealmSchemaEditor from '../components/RealmSchemaEditor/RealmSchemaEditor';
import PrivacySection from '../components/PrivacySection';
import InputParametersBuilder from '../components/InputParametersBuilder';
import WebhookEventsSelector from '../components/WebhookEventsSelector';
import { CATEGORIES, CATEGORY_HELP } from './dashboard/CategoryField';
import YamlPreview from '../components/YamlPreview';
import DLRCallbackPreview from '../components/DLRCallbackPreview';
import { buildDLRFromContract } from '../constants/formA';
import { dlrInputsToEditorState, editorStateToDlrInputs } from '../utils/dlrEditorBridge';
import { generateDLRYaml, parseDLRYaml } from '../utils/dlrYaml';
import { Button, Input, Select, Textarea, Card, useToast } from '../components/common';
import { copyText } from '../utils/clipboard';
import DeveloperOverview from './DeveloperOverview';
import MyAppsTab from './dashboard/MyAppsTab';
import DevSchemasTab from './dashboard/DevSchemasTab';
import CapabilitiesTab from './dashboard/CapabilitiesTab';
// DocsPage now lives at /documentation (DocumentationPage) — no longer a dashboard tab.
// ChangelogTab now lives at /releases (ReleasesPage) — no longer a dashboard tab.
import '../styles/Dashboard.css';
import '../styles/GraphView.css'; // provides .filter-bar / .filter-bars used by MyAppsTab
import './DeveloperDashboard.css';
import '../styles/PortalPage.css';
import CliPage from './CliPage';
import TokensPage from './TokensPage';

/**
 * Tabs whose content is a hub — a leaf column that divides the full height and
 * a pane beside it. They get no page padding and no max-width, and the height
 * chain above them has to be *definite* or the leaves collapse to their text.
 * One list, read by both the fill modifier and the full-bleed one, so the two
 * can never disagree about which pages are hubs.
 */
const HUB_TABS = ['my-apps', 'my-schemas', 'capabilities', 'get-started'];

function DeveloperDashboard({ groupId = null, initialTab, initialMode, initialType }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const toast = useToast();

  const [activeTab, setActiveTab] = useState(initialTab || 'my-apps');

  // Step 2: token state
  const [cliTokens, setCliTokens] = useState([]);
  const [step2Mode, setStep2Mode] = useState('pick'); // 'pick' | 'create'
  const [step2TokenName, setStep2TokenName] = useState('');
  const [step2Creating, setStep2Creating] = useState(false);
  const [step2NewToken, setStep2NewToken] = useState(null); // raw token after create

  const loadCliTokens = useCallback(async () => {
    try {
      const data = await api.listDeveloperTokens();
      const active = (data.tokens || []).filter(t => !t.is_revoked && !t.is_expired);
      setCliTokens(active);
    } catch { /* endpoint may not be deployed yet */ }
  }, []);

  useEffect(() => { loadCliTokens(); }, [loadCliTokens]);

  const handleStep2Create = async () => {
    const name = step2TokenName.trim();
    if (!name) { toast.error('Token name is required.'); return; }
    setStep2Creating(true);
    try {
      const data = await api.createDeveloperToken(name, 90, groupId || null);
      setStep2NewToken(data.token);
      setStep2TokenName('');
      setStep2Mode('pick');
      await loadCliTokens();
    } catch (err) {
      toast.error(err.message || 'Failed to create token.');
    } finally {
      setStep2Creating(false);
    }
  };

  const appliedUrlTypeRef = useRef(null);
  useEffect(() => {
    // Props from clean URL routes take priority
    const MODES = ['assistant', 'manual', 'tokens'];
    if (initialTab) {
      setActiveTab(initialTab);
      // An explicit ?mode= wins over the route's default. `cli` was its own tab until
      // it turned out to answer the same question as `assistant`; /build/cli still
      // redirects here, so map the old value onto the flow that absorbed it.
      const rawMode = new URLSearchParams(location.search).get('mode');
      const urlMode = rawMode === 'cli' ? 'assistant' : rawMode;
      const mode = MODES.includes(urlMode) ? urlMode : initialMode;
      if (MODES.includes(mode)) setGetStartedMode(mode);
      if ((initialType === 'app' || initialType === 'plugin') && appliedUrlTypeRef.current !== initialType) {
        appliedUrlTypeRef.current = initialType;
        lastRegType.current = initialType;
        setBuildType(initialType);
        setRegForm(makeDefaultRegForm(initialType));
      }
      return;
    }
    // Fallback: legacy ?tab= query params
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab) setActiveTab(tab);
    const mode = params.get('mode');
    if (MODES.includes(mode)) setGetStartedMode(mode);
    const type = params.get('type');
    if ((type === 'app' || type === 'plugin') && appliedUrlTypeRef.current !== type) {
      appliedUrlTypeRef.current = type;
      lastRegType.current = type;
      setBuildType(type);
      setRegForm(makeDefaultRegForm(type));
    }
    if (!type) appliedUrlTypeRef.current = null;
  }, [location.search, initialTab, initialMode, initialType]);

  // When set, Manual Registration is scoped to one type and the App/Plugin toggle is hidden.
  const regScope = new URLSearchParams(location.search).get('type');

  const [thirdPartyApps, setThirdPartyApps] = useState([]);
  const [loading, setLoading] = useState(false);

  // Plugin state
  const [myPlugins, setMyPlugins] = useState([]);
  const [availablePlugins, setAvailablePlugins] = useState([]);

  // Get Started mode: 'assistant' (default) or 'manual'
  const [getStartedMode, setGetStartedMode] = useState('assistant');

  // One command instead of two: install and log in are a single shell step, so the
  // developer copies once. The token is substituted when Step 1 has just minted one.
  const setupToken = step2NewToken || '<YOUR_TOKEN>';
  const setupCommand =
    `pipx install --force paradigm-cli --pip-args="--extra-index-url ` +
    `https://paradigm:${setupToken}@api.ofself.com/api/pypi/simple/" && ` +
    `paradigm login --token ${setupToken}${groupId ? ` --group-id ${groupId}` : ''}`;

  // Scaffolding used to be a terminal step, which made the assistant tab open with a
  // command. `paradigm init` is something the assistant can run, so it belongs here.
  // Nothing else does: init writes the skills that teach the assistant the rest, and
  // pointing at them by path would leak our file layout into a prompt a developer reads.
  // Which lane. The manual form has asked this since it existed; the assistant lane
  // did not, so it handed every developer an app-shaped prompt and a CRUX-shaped
  // step list — including the ones who arrived from the Plugins page.
  const [buildType, setBuildType] = useState(initialType === 'plugin' ? 'plugin' : 'app');

  // App or plugin is ONE question, asked once, in the fork column. The two
  // lanes used to ask it separately — `buildType` for the assistant,
  // `regForm.type` for the form — so switching lanes could silently change
  // what you were building. Setting both keeps them from disagreeing.
  const selectBuildType = (type) => {
    setBuildType(type);
    setRegForm(prev => ({ ...prev, type }));
  };

  // The question is asked IN the step it changes (owner call 2026-09-19,
  // retiring the fork column): steps 1 and 2 are identical for an app and a
  // plugin, so a whole drawer level spent on the choice folded the lanes
  // column to make room for two words. The switch now sits in step 3 of the
  // assistant lane and at the head of the manual form — hidden when the URL
  // already scoped the type.
  const showTypeSwitch = !regScope;
  const TYPE_OPTIONS = [
    { value: 'app', label: 'Application' },
    { value: 'plugin', label: 'Plugin' },
  ];

  const ASSISTANT_PROMPT = buildType === 'plugin'
    ? 'I want to build an ofSelf plugin that [your idea].\n\n' +
      'Scaffold it with `paradigm init --type plugin --name <short-name>`.'
    : 'I want to build an ofSelf app that [your idea].\n\n' +
      'Scaffold it with `paradigm init --name <short-name>`.';

  // Remember last registration type across submissions
  const lastRegType = useRef('app');

  // Unified registration form (app or plugin)
  const makeDefaultRegForm = (type) => {
    const effectiveType = type || lastRegType.current;
    return {
    type: effectiveType,
    name: '',
    description: '',
    developer_email: '',
    category: '',
    // App-only
    website_url: '',
    deployment_mode: 'incubator',
    app_store_visibility: 'selected_users',
    allowed_user_ids: [],
    redirect_uri: '',
    webhook_url: '',
    webhook_events: [],
    // Plugin-only
    endpoint_url: '',
    endpoint_mode: 'single',
    plugin_endpoint_base_url: '',
    execution_type: 'endpoint',
    mcp_sources: [{ id: 'src_1', url: '', tools: [], discovering: false }],
    tools: [],
    parent_app_id: '',
    usage_guide: '',
    // Shared — unified request model
    dlr_inputs: [],
    input_parameters: [],
    schema_grants: null,
    };
  };
  const [regForm, setRegForm] = useState(() => makeDefaultRegForm());
  const [allowlistQuery, setAllowlistQuery] = useState('');
  const [allowlistResults, setAllowlistResults] = useState([]);
  const [allowlistLoading, setAllowlistLoading] = useState(false);
  const [allowlistUserMap, setAllowlistUserMap] = useState({});

  // Newly created app credentials (shown once after registration)
  const newAppRef = useRef(null);
  const [newlyCreatedApp, setNewlyCreatedApp] = useState(null);

  // Schemas available for app registration (global public + dev's private)
  const [appSchemas, setAppSchemas] = useState([]);
  const [permTab, setPermTab] = useState('form'); // 'form' | 'yaml'
  const [dlrEditorKey, setDlrEditorKey] = useState(0); // bumped on YAML import to remount RSE
  const [yamlSubTab, setYamlSubTab] = useState('view'); // 'view' | 'paste'
  const [yamlPasteText, setYamlPasteText] = useState('');
  const [yamlErrors, setYamlErrors] = useState([]);
  const [yamlImportSuccess, setYamlImportSuccess] = useState(false);

  // Resizable DLR preview panel
  const [previewWidth, setPreviewWidth] = useState(300);
  const draggingRef = useRef(false);

  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    draggingRef.current = true;
    const startX = e.clientX;
    const startW = previewWidth;

    const onMove = (ev) => {
      if (!draggingRef.current) return;
      const delta = startX - ev.clientX; // dragging left = bigger preview
      const newW = Math.max(200, Math.min(600, startW + delta));
      setPreviewWidth(newW);
    };
    const onUp = () => {
      draggingRef.current = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [previewWidth]);
  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'overview' || activeTab === 'get-started' || activeTab === 'my-apps') {
        const myAppsUrl = groupId
          ? `/third-party/my-apps?group_id=${groupId}`
          : '/third-party/my-apps';
        const [appsResponse, pluginsData, schemasResponse, allPluginsData] = await Promise.all([
          api.request(myAppsUrl).catch(() => ({ apps: [] })),
          api.getMyPlugins(groupId).catch(() => []),
          api.request('/schemas?limit=1000&group_by_uri=true').catch(() => {
            return { schemas: [] };
          }),
          api.getPlugins({}).catch(() => ({ plugins: [] }))
        ]);
        setThirdPartyApps(appsResponse.apps || []);
        setMyPlugins(pluginsData);
        setAppSchemas(schemasResponse.schemas || []);
        setAvailablePlugins(allPluginsData.plugins || []);
      }

    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const searchAllowlistUsers = async (query) => {
    const q = query.trim();
    setAllowlistQuery(query);
    if (!q || regForm.app_store_visibility !== 'selected_users') {
      setAllowlistResults([]);
      return;
    }
    setAllowlistLoading(true);
    try {
      const users = await api.searchUsers(q, 10);
      const selected = new Set(regForm.allowed_user_ids || []);
      const filtered = (users || []).filter((u) => {
        const id = u.user_id || u.id;
        return id && !selected.has(id);
      });
      setAllowlistResults(filtered);
    } catch {
      setAllowlistResults([]);
    } finally {
      setAllowlistLoading(false);
    }
  };

  const addAllowlistedUser = (u) => {
    const id = u.user_id || u.id;
    if (!id) return;
    if ((regForm.allowed_user_ids || []).includes(id)) return;
    setAllowlistUserMap((prev) => ({
      ...prev,
      [id]: u.username || u.email || 'User',
    }));
    setRegForm({
      ...regForm,
      allowed_user_ids: [...(regForm.allowed_user_ids || []), id],
    });
    setAllowlistQuery('');
    setAllowlistResults([]);
  };

  const removeAllowlistedUser = (id) => {
    setRegForm({
      ...regForm,
      allowed_user_ids: (regForm.allowed_user_ids || []).filter((x) => x !== id),
    });
  };

  // Approve dialog state
  // buildDLRFromContract imported from constants/formA

  const handleRegister = async (e) => {
    e.preventDefault();
    setNewlyCreatedApp(null);

    try {
      {
        if (!regForm.redirect_uri.trim()) {
          toast.error('Redirect URI is required.');
          return;
        }
        const dlr = buildDLRFromContract(regForm);
        // Generate canonical YAML — this is the source of truth for DLR fields
        const dlrYaml = generateDLRYaml(dlr, {
          dlrInputs: regForm.dlr_inputs || [],
          inputParameters: regForm.input_parameters || [],
          schemaGrants: regForm.schema_grants || null,
          schemaNameMap: Object.fromEntries(appSchemas.map(s => [String(s.id), s.schema_name])),
        });
        const result = await api.registerThirdParty(
          regForm.name,
          regForm.description,
          [regForm.redirect_uri.trim()],
          regForm.developer_email,
          regForm.website_url,
          null,
          {
            yaml: dlrYaml,
            deployment_mode: regForm.deployment_mode,
            app_store_visibility: regForm.app_store_visibility,
            allowed_user_ids: regForm.app_store_visibility === 'selected_users' ? (regForm.allowed_user_ids || []) : [],
            group_id: groupId || undefined,
            category: regForm.category || undefined,
          }
        );

        setThirdPartyApps([...thirdPartyApps, result]);
        setNewlyCreatedApp(result);
        toast.success('App registered successfully! Save your credentials below \u2014 they will not be shown again.');
        lastRegType.current = 'app';
        setRegForm(makeDefaultRegForm('app'));

        setTimeout(() => {
          if (newAppRef.current) {
            newAppRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Redirect URI handlers replaced by RedirectUrisInput component

  // Credentials here are shown once, so only say "copied" when it really was.
  const copyToClipboard = async (text, label) => {
    if (await copyText(text)) toast.success(`${label} copied to clipboard!`);
    else toast.error(`Could not copy the ${label}. Select it and copy it yourself.`);
  };

  return (
    <div className={`dashboard ${['get-started', 'docs'].includes(activeTab) ? 'dashboard--white' : ''}${initialTab ? ' dashboard--standalone' : ''}${HUB_TABS.includes(activeTab) ? ' dashboard--fill' : ''}`}>
      {/* Tabs are in the sidebar */}

      <div className={`dashboard-content ${HUB_TABS.includes(activeTab) ? 'dashboard-content--full' : ''}`}>
        {/* My Applications — one clean scrolling page: usage analytics on top,
            then the app/plugin hub (Register cards + Your Apps/Plugins). */}
        {activeTab === 'my-apps' && (
          <div className="dev-apps-page">
            <DeveloperOverview groupId={groupId} analyticsOnly />
            <MyAppsTab
              thirdPartyApps={thirdPartyApps}
              myPlugins={myPlugins}
              appSchemas={appSchemas}
              availablePlugins={availablePlugins}
              loading={loading}
              loadData={loadData}
            />
          </div>
        )}


        {/* Get Started Tab */}
        {activeTab === 'get-started' && (
          <div className="dev-getstarted">
            {/* THE LANES STAND IN A COLUMN (owner call), not a strip across
                the head: the kit's HubSpread, the same selector the schemas
                hub wears — leaf cells dividing the column's full height,
                seated beside the nav column, with the lane's work in the pane.
                Its leaves carry the fleet register for free. */}
            <HubSpread
              leaves={[
                { id: 'assistant', name: 'With an assistant' },
                { id: 'manual', name: 'Register manually' },
                { id: 'tokens', name: 'Tokens' },
              ]}
              active={getStartedMode}
              onSelect={setGetStartedMode}
              resizable
              storageKey="dev_getstarted_lanes_w"
            >
            <div className="dev-getstarted__body hub__pane-scroll">

            {/* ── Coding Assistant mode — no intro, straight to the steps.
                (The intro band retired 2026-08-22: the fork leaves' own meta
                lines already say what each lane is, so the paragraph over the
                steps repeated the column beside it.) ── */}
            {getStartedMode === 'assistant' && (
              <div className="dev-dash__get-started">
                <div className="dev-dash__get-started-steps">
                  {/* Step 1: Create token */}
                  <div className="dev-dash__get-started-step">
                    <div className="dev-dash__get-started-step-number">1</div>
                    <div className="dev-dash__get-started-step-content">
                      <h3>Create a developer token</h3>
                      {step2NewToken ? (
                        <>
                          <p className="dev-dash__step2-warning">
                            Save this token — it won't be shown again.
                          </p>
                          <div className="dev-dash__get-started-code">
                            <code>{step2NewToken}</code>
                            <button
                              className="dev-dash__get-started-copy"
                              onClick={() => copyToClipboard(step2NewToken, 'Token')}
                            >
                              Copy
                            </button>
                          </div>
                          <button type="button" className="dev-dash__inline-link" onClick={() => setStep2NewToken(null)}>
                            Dismiss
                          </button>
                        </>
                      ) : step2Mode === 'create' ? (
                        <div className="dev-dash__step2-create">
                          <input
                            type="text"
                            className="dev-dash__step2-input"
                            placeholder="Token name (e.g. MacBook Pro, CI)"
                            value={step2TokenName}
                            onChange={e => setStep2TokenName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleStep2Create()}
                            maxLength={100}
                            autoFocus
                          />
                          <button
                            type="button"
                            className="dev-dash__step2-create-btn"
                            onClick={handleStep2Create}
                            disabled={step2Creating || !step2TokenName.trim()}
                          >
                            {step2Creating ? 'Creating…' : 'Create'}
                          </button>
                          <button type="button" className="dev-dash__inline-link" onClick={() => setStep2Mode('pick')}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="dev-dash__step2-actions">
                          <p>Your token authenticates the CLI and package downloads. One token per machine.</p>
                          <button type="button" className="dev-dash__step2-create-btn" onClick={() => setStep2Mode('create')}>
                            Create a token
                          </button>
                          {cliTokens.length > 0 && (
                            <p className="dev-dash__step-hint">
                              Already have one? Reuse an existing token from the{' '}
                              <button type="button" className="dev-dash__inline-link" onClick={() => navigate('/tokens')}>
                                Tokens page
                              </button>.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Step 2: Install + login */}
                  <div className="dev-dash__get-started-step">
                    <div className="dev-dash__get-started-step-number">2</div>
                    <div className="dev-dash__get-started-step-content">
                      <h3>Install the CLI &amp; log in <span className="dev-dash__lane">you, in a terminal</span></h3>
                      <p className="dev-dash__step-hint" style={{ marginBottom: 8, marginTop: 0 }}>
                        One command. Requires <a href="https://pipx.pypa.io" target="_blank" rel="noreferrer" className="dev-dash__inline-link">pipx</a> (<code>brew install pipx</code>).
                      </p>
                      <div className="dev-dash__get-started-code">
                        <code>{setupCommand}</code>
                        <button
                          className="dev-dash__get-started-copy"
                          onClick={() => copyToClipboard(setupCommand, 'Command')}
                        >
                          Copy
                        </button>
                      </div>
                      {!step2NewToken && (
                        <p className="dev-dash__step-hint">Create a token in Step 1 — it auto-fills here.</p>
                      )}
                      <details className="dev-dash__step-details" style={{ marginTop: 12 }}>
                        <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary, #666)' }}>
                          Building a frontend app with <code>@ofself/shell</code>?
                        </summary>
                        <p className="dev-dash__step-hint" style={{ marginTop: 8 }}>
                          The same token installs it from the ofself npm registry. <code>paradigm login</code> saves it
                          {' '}for npm, and <code>paradigm init --frontend</code> writes the project's <code>.npmrc</code>. By hand:
                        </p>
                        <div className="dev-dash__get-started-code">
                          <code style={{ whiteSpace: 'pre' }}>{'# .npmrc in your project, committed\n@ofself:registry=https://api.ofself.ai/api/npm/\n\n# your token, in ~/.npmrc, never committed\nnpm config set //api.ofself.ai/api/npm/:_authToken YOUR_DEV_TOKEN\n\nnpm install @ofself/shell'}</code>
                        </div>
                      </details>
                    </div>
                  </div>

                  {/* Step 3: hand off — scaffolding happens inside the assistant */}
                  <div className="dev-dash__get-started-step">
                    <div className="dev-dash__get-started-step-number">3</div>
                    <div className="dev-dash__get-started-step-content">
                      <h3>Hand it to your assistant <span className="dev-dash__lane">the assistant, from here on</span></h3>
                      <p>
                        Open an empty folder in VS Code or Cursor, start Claude Code, and paste this.
                        It scaffolds the project and designs the {buildType === 'plugin' ? 'plugin' : 'app'} with you — no more commands to copy.
                      </p>
                      {/* THE ONLY STEP THAT FORKS: the prompt names the init
                          command, and that is the whole difference between
                          building an app and a plugin. So the switch stands
                          here, beside the thing it changes. */}
                      {showTypeSwitch && (
                        <SegmentedControl
                          className="dev-dash__type-switch"
                          aria-label="What are you building?"
                          options={TYPE_OPTIONS}
                          value={buildType}
                          onChange={selectBuildType}
                        />
                      )}
                      <div className="dev-dash__get-started-code">
                        <code style={{ whiteSpace: 'pre-wrap' }}>{ASSISTANT_PROMPT}</code>
                        <button
                          className="dev-dash__get-started-copy"
                          onClick={() => copyToClipboard(ASSISTANT_PROMPT, 'Prompt')}
                        >
                          Copy
                        </button>
                      </div>
                      <p className="dev-dash__step-hint">
                        {buildType === 'plugin' ? (
                          <>
                            <code>paradigm init --type plugin</code> writes your manifest
                            (<code>plugin.yaml</code>), a starter <code>plugin_handler.py</code> with the
                            signature check already in it, and the skills that teach the assistant how to
                            build on ofSelf. No <code>CRUX.md</code> — a plugin's contract is its manifest.
                          </>
                        ) : (
                          <>
                            <code>paradigm init</code> writes your design doc (<code>CRUX.md</code>), a starter
                            <code>app.py</code>, and the skills that
                            teach the assistant how to build on ofSelf. The prompt tells it to read those skills
                            directly, so they take effect immediately rather than on the next restart.
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                {/* The terminal path — same job, no assistant. Folded in rather than
                    given its own tab, because it answered the same question twice. */}
                <details className="dev-dash__get-started-details">
                  <summary>Prefer to drive the CLI yourself?</summary>
                  <div className="dev-dash__get-started-details-content">
                    <p className="dev-dash__step-hint">
                      Steps 1 and 2 above are the same. From there, run these instead of pasting the prompt —
                      this is every command the assistant would have run for you.
                    </p>
                    <CliPage embedded />
                  </div>
                </details>

                {/* Token refresh note */}
                <div className="dev-dash__get-started-refresh">
                  <h4>Refreshing your CLI session</h4>
                  <p>To switch to a new token or update an expired one, just run <code>paradigm login</code> again:</p>
                  <div className="dev-dash__get-started-code">
                    <code>paradigm login --token &lt;new-token&gt;</code>
                  </div>
                  <p className="dev-dash__step-hint">
                    Create and revoke tokens anytime on the{' '}
                    <button type="button" className="dev-dash__inline-link" onClick={() => navigate('/tokens')}>
                      Tokens page
                    </button>.
                  </p>
                </div>

                {/* Under the hood */}
                <details className="dev-dash__get-started-details">
                  <summary>What happens under the hood</summary>
                  <div className="dev-dash__get-started-details-content">
                    {buildType === 'plugin' ? (
                      <p>
                        Step 3 (<code>paradigm init --type plugin</code>) scaffolds
                        <code>plugin.yaml</code>, <code>plugin_handler.py</code>, the developer
                        guides in <code>docs/</code>, and the <code>paradigm-plugin</code> Claude Code skill. There is no design-doc
                        phase: each of a plugin's tools declares what it reads and writes, and that
                        declaration is the whole contract.
                      </p>
                    ) : (
                      <p>
                        Step 3 (<code>paradigm init</code>) scaffolds <code>CRUX.md</code>,
                        <code>app.py</code>, the developer guides in <code>docs/</code>, and the
                        <code>paradigm-app</code> Claude Code skill — which designs the app with you in
                        Phase 0 before building it. Add <code>--frontend</code> for a React + Vite +
                        <code>@ofself/shell</code> scaffold.
                      </p>
                    )}
                    <p>From there the assistant drives the CLI through these steps:</p>
                    {buildType === 'plugin' ? (
                    <ol>
                      <li><code>paradigm schema search</code> — surveys the registry <em>before</em> deciding what the plugin reads, so it reuses a schema rather than inventing one only it can read</li>
                      <li>fills in <code>plugin.yaml</code> — <code>reads</code> (what it is handed), <code>writes</code> (what it may write back), <code>inputs</code> (Settings, asked once when someone sets it up; or Inputs, with <code>scope: runtime</code>, asked each run), and <code>tools</code> when it does more than one thing, each tool declaring its own. A plugin with no tools is one tool</li>
                      <li><code>paradigm plugin check</code> — validates the manifest offline, before anything is registered</li>
                      <li><code>paradigm plugin push</code> — registers it, and prints the signing secret once</li>
                      <li><code>paradigm plugin health</code> — asks ofSelf to call your endpoint, which is the question that matters: the platform calls in from its own servers, so a plugin on localhost is unreachable however well it works for you</li>
                      <li>writes your <code>plugin_handler.py</code> — verifying the signature against the raw body, and returning <code>output_nodes</code> the platform checks against what you declared</li>
                    </ol>
                    ) : (
                    <ol>
                      <li><code>paradigm schema search</code> — surveys the registry <em>before</em> asking you anything, so every question is anchored to schemas that actually exist</li>
                      <li><code>paradigm crux questions</code> — the interview that fills in <code>CRUX.md</code>, your design doc. §4 is the one that matters: for each data type, reuse an existing schema or invent one</li>
                      <li><code>paradigm crux validate</code> — gates on the design being settled before anything is registered</li>
                      <li><code>paradigm crux sync</code> — pushes the <code>CRUX.md</code> §9 DLR to the app record. The DLR is generated from the design, not written by hand</li>
                      <li><code>paradigm validate</code> — checks the not-yet-registered app spec offline (once registered, the spec lives on the app record — use <code>paradigm dlr check</code> for server-side DLR validation)</li>
                      <li><code>paradigm app push</code> — registers your app with ofSelf</li>
                      <li>Writes your <code>app.py</code> with real SDK calls against the schemas you chose</li>
                    </ol>
                    )}
                    <p>
                      Your scaffolded project includes the developer guides in <code>docs/</code>
                      (<code>docs/index.md</code> routes them; <code>docs/concepts.md</code> first if
                      oS is new to you). The assistant reads them as it builds.
                    </p>
                  </div>
                </details>
              </div>
            )}

            {/* ── Manual Registration mode ── */}

            {/* A plugin is registered in its own workspace, which shows the
                signing secret once and everything the form here never did. */}
            {getStartedMode === 'manual' && regForm.type === 'plugin' && (
              <Navigate to="/plugins/new" replace />
            )}
            {getStartedMode === 'manual' && regForm.type !== 'plugin' && (
              <div>

            {/* THE FORM IS A STACK OF BLOCKS.
                The kit's form-folio (<Forms>, @ofself/shell) in panel mode — no
                statement leaf; the type switch is the stack's first cell.
                Every field is its own cell: the name and the sentence saying
                what to put there seated INSIDE the block, the control bare
                beneath it, and a hairline seam where the next block meets it.
                The same grammar the sign-in page wears, because it is the same
                component. Anything that is not a single control (the webhook
                picker, the allowlist, the DLR editor) is a Forms.Block, so it
                still stands in the stack as one cell. */}
            <Forms dense onSubmit={handleRegister} className="dev-dash__regform">
                    {/* WHAT IS BEING REGISTERED — the first cell of the stack,
                        since the fork column that used to say it is gone. The
                        same setter as the assistant lane's switch, so the two
                        lanes cannot disagree about what you are building. */}
                    {showTypeSwitch && (
                      <Forms.Block className="dev-dash__regtype">
                        <p className="dev-dash__form-label">Type</p>
                        <SegmentedControl
                          className="dev-dash__type-switch"
                          aria-label="Registration type"
                          options={TYPE_OPTIONS}
                          value={regForm.type}
                          onChange={selectBuildType}
                        />
                      </Forms.Block>
                    )}
                    <Input
                      block
                      label="Name"
                      type="text"
                      placeholder="e.g. Health Tracker"
                      value={regForm.name}
                      onChange={(e) => setRegForm({ ...regForm, name: e.target.value })}
                      required
                      helpText="The public name users will see during authorization."
                    />

                  <Textarea
                    block
                    label="Description"
                    placeholder="What does your app do?"
                    value={regForm.description}
                    onChange={(e) => setRegForm({ ...regForm, description: e.target.value })}
                    rows={2}
                    helpText="Shown to users in the app store and during authorization. Keep it concise."
                  />

                  <Select
                    block
                    label="Category"
                    value={regForm.category}
                    onChange={(e) => setRegForm({ ...regForm, category: e.target.value })}
                    placeholder="Not chosen"
                    options={CATEGORIES}
                    optional
                    helpText={CATEGORY_HELP}
                  />

                  {regForm.type === 'app' && (
                    <>
                      <Input
                        block
                        label="Website URL"
                        type="url"
                        placeholder="https://yourapp.com"
                        value={regForm.website_url}
                        onChange={(e) => setRegForm({ ...regForm, website_url: e.target.value })}
                        optional
                        helpText="Your app's public homepage. Shown in the app store listing."
                      />
                      <Input
                        block
                        label="Redirect URI"
                        type="url"
                        placeholder="https://yourapp.com/oauth/callback"
                        value={regForm.redirect_uri}
                        onChange={(e) => setRegForm({ ...regForm, redirect_uri: e.target.value })}
                        required
                        helpText="Where users are sent after authorizing. Must match your OAuth callback handler."
                      />
                    </>
                  )}

                  <Input
                    block
                    label="Developer Email"
                    type="email"
                    placeholder="Leave empty to use your account email"
                    value={regForm.developer_email}
                    onChange={(e) => setRegForm({ ...regForm, developer_email: e.target.value })}
                    optional
                    helpText="Contact email for this app. Falls back to your account email if left blank."
                  />

                  {regForm.type === 'app' && (
                    /* Emits its own cells: the URL is a single control, so it
                       is a field like any other rather than a field buried
                       inside a block. */
                    <WebhookEventsSelector
                      block
                      webhookUrl={regForm.webhook_url || ''}
                      onWebhookUrlChange={(url) => setRegForm({ ...regForm, webhook_url: url })}
                      selectedEvents={regForm.webhook_events || []}
                      onEventsChange={(events) => setRegForm({ ...regForm, webhook_events: events })}
                    />
                  )}
                  {(
                    <>
                      <Select
                        block
                        label="Deployment Mode"
                        value={regForm.deployment_mode}
                        onChange={(e) => {
                          const mode = e.target.value;
                          setRegForm({
                            ...regForm,
                            deployment_mode: mode,
                            app_store_visibility: mode === 'public' ? 'all_users' : 'selected_users',
                            ...(mode === 'public' ? { allowed_user_ids: [] } : {}),
                          });
                        }}
                        options={[
                          { value: 'incubator', label: 'Incubator (Private)' },
                          { value: 'public', label: 'Public (App Store)' },
                        ]}
                        helpText="Incubator apps are only visible to users you allow. Public apps are listed in the app store for everyone."
                      />
                      {regForm.deployment_mode === 'incubator' && (
                        <Forms.Block className="dev-dash__regform-allowlist">
                          {/* Search-and-pick, not a single control — one cell
                              holding the query, its results and the chips. */}
                          <label className="dev-dash__form-label dev-dash__form-label--small">Allowed Users</label>
                          <Input
                            type="text"
                            placeholder="Search users by name/email"
                            value={allowlistQuery}
                            onChange={(e) => searchAllowlistUsers(e.target.value)}
                          />
                          {allowlistLoading && <p style={{ fontSize: 12, marginTop: 6 }}>Searching users...</p>}
                          {!allowlistLoading && allowlistResults.length > 0 && (
                            <div style={{ border: '1px solid var(--border-color)', borderRadius: 8, marginTop: 6, maxHeight: 180, overflowY: 'auto' }}>
                              {allowlistResults.map((u) => (
                                <button
                                  key={u.user_id || u.id}
                                  type="button"
                                  onClick={() => addAllowlistedUser(u)}
                                  style={{ width: '100%', textAlign: 'left', padding: '8px 10px', border: 'none', background: 'transparent', cursor: 'pointer' }}
                                >
                                  <strong>{u.username || u.email || 'User'}</strong>
                                  <div style={{ fontSize: 12, opacity: 0.8 }}>{u.email || (u.user_id || u.id)}</div>
                                </button>
                              ))}
                            </div>
                          )}
                          {(regForm.allowed_user_ids || []).length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                              {(regForm.allowed_user_ids || []).map((id) => (
                                <button
                                  key={id}
                                  type="button"
                                  onClick={() => removeAllowlistedUser(id)}
                                  style={{ border: '1px solid var(--border-color)', borderRadius: 999, padding: '4px 8px', background: 'var(--bg-secondary)', cursor: 'pointer' }}
                                  title="Remove user"
                                >
                                  {(allowlistUserMap[id] || 'User')} ({id}) x
                                </button>
                              ))}
                            </div>
                          )}
                        </Forms.Block>
                      )}
                    </>
                  )}

              {/* Data Lender Request — the editor and its live consent preview
                  are a workbench, so they stand as one cell. */}
              <Forms.Block className="dev-dash__regform-dlr">
                <div className="dev-dash__perm-header">
                  <h3 className="dev-dash__perm-title">Data Lender Request</h3>
                  <div className="dev-dash__perm-tabs">
                    {[
                      { key: 'form', label: 'Form' },
                      { key: 'yaml', label: 'YAML' },
                    ].map(tab => (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => setPermTab(tab.key)}
                        className={`dev-dash__perm-tab ${permTab === tab.key ? 'dev-dash__perm-tab--active' : 'dev-dash__perm-tab--inactive'}`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>

                {(() => {
                  const { verbCaps: dlrVerbCaps, scopeFilters: dlrScopeFilters } = dlrInputsToEditorState(regForm.dlr_inputs);
                  // Live YAML preview for the RSE YAML tab
                  let dlrLiveYaml = '';
                  try {
                    const dlrObj = buildDLRFromContract(regForm);
                    dlrLiveYaml = generateDLRYaml(dlrObj, {
                      dlrInputs: regForm.dlr_inputs || [],
                      inputParameters: regForm.input_parameters || [],
                      schemaGrants: regForm.schema_grants || null,
                      schemaNameMap: Object.fromEntries(appSchemas.map(s => [String(s.id), s.schema_name])),
                      pluginNameMap: Object.fromEntries(availablePlugins.map(p => [String(p.id), p.name])),
                    });
                  } catch { /* half-filled form: no preview yet */ }
                  return (
                  <div className="dev-dash__dlr-split">
                    <div className="dev-dash__dlr-editor">
                      {permTab === 'form' ? (
                        <>
                          <div className="dev-dash__perm-requests-box">
                            <p className="dev-dash__perm-requests-desc">
                              Declare what your app needs. Users will be asked to grant exactly these permissions during authorization.
                            </p>
                            <PrivacySection label="Data Access Request">
                              <RealmSchemaEditor
                                key={dlrEditorKey}
                                verbCaps={dlrVerbCaps}
                                scopeFilters={dlrScopeFilters}
                                schemas={appSchemas}
                                schemaGrants={regForm.schema_grants}
                                onSchemaGrantsChange={(grants) => setRegForm(prev => ({ ...prev, schema_grants: grants }))}
                                onChange={(newVerbCaps, newScopeFilters) =>
                                  setRegForm(prev => ({ ...prev, dlr_inputs: editorStateToDlrInputs(newVerbCaps, newScopeFilters, prev.dlr_inputs || []) }))
                                }
                                mode="edit"
                                emptyHint="no access requested yet"
                                nodeFilterLabel="Restrict to schema"
                                context="dlr"
                                yamlPreview={dlrLiveYaml}
                              />
                            </PrivacySection>
                          </div>

                          {/* Variables — user-facing prompts filled at auth time */}
                          <div className="dev-dash__perm-requests-box" style={{ marginTop: 16 }}>
                            <h5 className="dev-dash__perm-requests-title">Variables</h5>
                            <p className="dev-dash__perm-requests-desc">
                              Define variables that users fill in during authorization (e.g. a target node, date range, or selection).
                            </p>
                            <InputParametersBuilder
                              items={regForm.input_parameters}
                              onChange={(params) => setRegForm(prev => ({ ...prev, input_parameters: params }))}
                              schemas={appSchemas}
                            />
                          </div>
                        </>
                      ) : (
                        <div>
                          {/* Sub-toggle: View | Paste & Import */}
                          <div className="dev-dash__yaml-subtabs">
                            {[
                              { key: 'view', label: 'View' },
                              { key: 'paste', label: 'Paste & Import' },
                            ].map(st => (
                              <button
                                key={st.key}
                                type="button"
                                onClick={() => { setYamlSubTab(st.key); setYamlErrors([]); setYamlImportSuccess(false); }}
                                className={`dev-dash__yaml-subtab${yamlSubTab === st.key ? ' dev-dash__yaml-subtab--active' : ''}`}
                              >
                                {st.label}
                              </button>
                            ))}
                          </div>

                          {yamlSubTab === 'view' ? (
                            <>
                              <p className="dev-dash__perm-yaml-desc">
                                Auto-generated DLR from your form declarations. Switch to the Form tab to edit, or use "Paste & Import" to import YAML directly.
                              </p>
                              <YamlPreview
                                yaml={generateDLRYaml(
                                  buildDLRFromContract(regForm),
                                  {
                                    schemaNameMap: Object.fromEntries(appSchemas.map(s => [s.id, s.schema_name])),
                                    pluginNameMap: Object.fromEntries(availablePlugins.map(p => [p.id, p.name])),
                                    dlrInputs: regForm.dlr_inputs,
                                    inputParameters: regForm.input_parameters,
                                    schemaGrants: regForm.schema_grants,
                                  }
                                )}
                                title="Data Lender Request YAML"
                                collapsible={false}
                                defaultExpanded={true}
                              />
                            </>
                          ) : (
                            <>
                              <p className="dev-dash__perm-yaml-desc">
                                Paste a pre-written DLR YAML below. Click "Validate & Import" to parse it and populate the form.
                              </p>
                              <textarea
                                value={yamlPasteText}
                                onChange={(e) => { setYamlPasteText(e.target.value); setYamlErrors([]); setYamlImportSuccess(false); }}
                                placeholder={'# Paste your DLR YAML here\nrequests:\n  - key: my_nodes\n    type: see_nodes\n    source: any\n    min: 1\n    max: unlimited'}
                                className={`dev-dash__yaml-paste${yamlErrors.length > 0 ? ' dev-dash__yaml-paste--error' : ''}`}
                              />
                              <div className="dev-dash__yaml-paste-actions">
                                <button
                                  type="button"
                                  onClick={() => {
                                    const { dlrInputs, inputParameters, schemaGrants, errors } = parseDLRYaml(yamlPasteText);
                                    if (errors.length > 0) {
                                      setYamlErrors(errors);
                                      setYamlImportSuccess(false);
                                    } else {
                                      setRegForm(prev => ({ ...prev, dlr_inputs: dlrInputs, input_parameters: inputParameters, ...(schemaGrants ? { schema_grants: schemaGrants } : {}) }));
                                      setDlrEditorKey(k => k + 1);
                                      setYamlErrors([]);
                                      setYamlImportSuccess(true);
                                      setTimeout(() => {
                                        setPermTab('form');
                                        setYamlImportSuccess(false);
                                      }, 1200);
                                    }
                                  }}
                                  disabled={!yamlPasteText.trim()}
                                  className={`dev-dash__yaml-import-btn${!yamlPasteText.trim() ? ' dev-dash__yaml-import-btn--disabled' : ''}`}
                                >
                                  Validate & Import
                                </button>
                                <button
                                  type="button"
                                  onClick={() => { setYamlPasteText(''); setYamlErrors([]); setYamlImportSuccess(false); }}
                                  className="dev-dash__yaml-clear-btn"
                                >
                                  Clear
                                </button>
                              </div>

                              {yamlImportSuccess && (
                                <div className="dev-dash__yaml-success">
                                  YAML imported successfully! Switching to Form tab...
                                </div>
                              )}

                              {yamlErrors.length > 0 && (
                                <div className="dev-dash__yaml-errors">
                                  <div className="dev-dash__yaml-errors-title">
                                    Validation failed ({yamlErrors.length} error{yamlErrors.length > 1 ? 's' : ''})
                                  </div>
                                  <ul className="dev-dash__yaml-errors-list">
                                    {yamlErrors.map((err, i) => (
                                      <li key={i}>{err}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <div
                      className="dev-dash__dlr-drag"
                      onMouseDown={handleDragStart}
                      title="Drag to resize"
                    />
                    <div className="dev-dash__dlr-preview" style={{ width: previewWidth }}>
                      <DLRCallbackPreview
                        appName={regForm.name || 'Your App'}
                        userName={user?.username || 'User'}
                        dlrInputs={regForm.dlr_inputs || []}
                        inputParameters={regForm.input_parameters || []}
                        schemaGrants={regForm.schema_grants}
                        schemas={appSchemas}
                        plugins={availablePlugins}
                        yamlPreview={dlrLiveYaml}
                      />
                    </div>
                  </div>
                  );
                })()}
              </Forms.Block>

              {/* The action IS its cell — the kit's ink slab, full bleed. */}
              <Forms.Cta>
                Register Application
              </Forms.Cta>
            </Forms>

            {/* Show app credentials after registration */}
            {newlyCreatedApp && (
              <Card ref={newAppRef} className="dev-dash__creds-box">
                <h4 className="dev-dash__creds-title">App Credentials (save these now!)</h4>
                <div className="dev-dash__creds-field">
                  <label className="dev-dash__creds-label">APP NAME</label>
                  <div className="dev-dash__creds-name">{newlyCreatedApp.name}</div>
                </div>
                <div className="dev-dash__creds-field">
                  <label className="dev-dash__creds-label">APP ID</label>
                  <div className="dev-dash__creds-value-row">
                    <code className="dev-dash__creds-code">{newlyCreatedApp.id}</code>
                    <Button variant="secondary" size="sm" onClick={() => copyToClipboard(newlyCreatedApp.id, 'App ID')}>Copy</Button>
                  </div>
                </div>
                <div className="dev-dash__creds-field">
                  <label className="dev-dash__creds-label">CLIENT ID</label>
                  <div className="dev-dash__creds-value-row">
                    <code className="dev-dash__creds-code">{newlyCreatedApp.client_id}</code>
                    <Button variant="secondary" size="sm" onClick={() => copyToClipboard(newlyCreatedApp.client_id, 'Client ID')}>Copy</Button>
                  </div>
                </div>
                {newlyCreatedApp.api_key && (
                  <div className="dev-dash__creds-field">
                    <label className="dev-dash__creds-label">API KEY</label>
                    <div className="dev-dash__creds-value-row">
                      <code className="dev-dash__creds-code">{newlyCreatedApp.api_key}</code>
                      <Button variant="secondary" size="sm" onClick={() => copyToClipboard(newlyCreatedApp.api_key, 'API Key')}>Copy</Button>
                    </div>
                  </div>
                )}
                {newlyCreatedApp.plugin_secret && (
                  <div className="dev-dash__creds-field">
                    <label className="dev-dash__creds-label">SIGNING SECRET</label>
                    <div className="dev-dash__creds-value-row">
                      <code className="dev-dash__creds-code">{newlyCreatedApp.plugin_secret}</code>
                      <Button variant="secondary" size="sm" onClick={() => copyToClipboard(newlyCreatedApp.plugin_secret, 'Signing secret')}>Copy</Button>
                    </div>
                  </div>
                )}
                {newlyCreatedApp.webhook_secret && (
                  <div className="dev-dash__creds-field">
                    <label className="dev-dash__creds-label">WEBHOOK SECRET</label>
                    <div className="dev-dash__creds-value-row">
                      <code className="dev-dash__creds-code">{newlyCreatedApp.webhook_secret}</code>
                      <Button variant="secondary" size="sm" onClick={() => copyToClipboard(newlyCreatedApp.webhook_secret, 'Webhook Secret')}>Copy</Button>
                    </div>
                  </div>
                )}
                <p className="dev-dash__creds-warning">
                  These credentials will not be shown again. Store them securely.
                </p>
              </Card>
            )}

              </div>
            )}

            {/* ── Tokens mode — the Tokens page folded in (owner call
                2026-08-14, consolidating the left bar). ── */}
            {getStartedMode === 'tokens' && <TokensPage embedded />}
            </div>
            </HubSpread>
          </div>
        )}

        {/* My Schemas Tab */}
        {activeTab === 'my-schemas' && (
          <DevSchemasTab />
        )}

        {/* API Docs (capabilities) Tab */}
        {activeTab === 'capabilities' && (
          <CapabilitiesTab groupId={groupId} />
        )}


        {/* Admin: Approve Users Tab */}
        {/* Admin: Verify Apps Tab */}
        {/* Admin: Stats Tab */}
        {/* Admin: Logs Tab */}
        {/* Admin: Login Attempts Tab */}
        {/* Admin: Azure Metrics Tab */}

      </div>
    </div>
  );
}

export default DeveloperDashboard;
